import type { TRIReportDataType } from '../../types/form-r.ts';
import type { ISO8601DateOnly } from '../../../cross-app-shared/types/common.ts';
import type { Facility, SimpleSelectable } from '../../types/db.ts';
import type { Warning } from '../../types/tri.ts';

export function formRCommonReportHeadersFromYear(
  year: number,
): Pick<
  TRIReportDataType,
  | 'SubmissionReportingYear'
  | 'ReportDueDate'
  | 'ReplacedReportIdentifier'
  | 'RevisionIndicator'
  | 'ChemicalReportRevisionCode'
  | 'ChemicalReportWithdrawalCode'
  | 'ReportIdentifier'
  | 'ReportMetaData'
  | 'SubmissionEntireFacilityIndicator'
  | 'SubmissionPartialFacilityIndicator'
  | 'SubmissionFederalFacilityIndicator'
  | 'SubmissionGOCOFacilityIndicator'
  | 'SubmissionAdditionalDataIndicator'
  | 'TRIComment'
  | 'OptionalInformationCategory'
  | 'OptionalInformationText'
  | 'MiscellaneousInformationCategory'
  | 'MiscellaneousInformationText'
> {
  return {
    SubmissionReportingYear: String(year),

    ReportDueDate: `${year + 1}-07-01` as ISO8601DateOnly,

    ReplacedReportIdentifier: [], // Not relevant unless we need to replace a previously filed report.
    RevisionIndicator: 'N',

    ChemicalReportRevisionCode: [], // Not relevant unless we need to revise a previously filed report.
    ChemicalReportWithdrawalCode: [], // Not relevant unless we need to withdraw a previously filed report.

    ReportIdentifier: [], // Presumably this is filled by TRI-MEweb
    ReportMetaData: null, // Presumably this is filled by TRI-MEweb

    // We currently assume we're always filing for an entire facility, and not a federal facility:
    SubmissionEntireFacilityIndicator: true,
    SubmissionPartialFacilityIndicator: false,
    SubmissionFederalFacilityIndicator: 'N',
    SubmissionGOCOFacilityIndicator: false,

    // For reporting years beginning in 1991, the indicator that shows whether additional optional information
    // on source reduction, pollution control, or recycling activities implemented during the reporting year or
    // prior years has been attached to the submission:
    SubmissionAdditionalDataIndicator: false,
    TRIComment: [],
    OptionalInformationCategory: null,
    OptionalInformationText: null,
    MiscellaneousInformationCategory: null,
    MiscellaneousInformationText: null,
  };
}

export function formRCommonReportHeadersFromFacility(
  facility: SimpleSelectable<Facility>,
  warnings: Warning[],
): Pick<
  TRIReportDataType,
  | 'CertifierName'
  | 'CertifierTitleText'
  | 'CertificationSignedDate'
  | 'PublicContactEmailAddressText'
  | 'PublicContactNameText'
  | 'PublicContactPhoneExtText'
  | 'PublicContactPhoneText'
  | 'TechnicalContactEmailAddressText'
  | 'TechnicalContactNameText'
  | 'TechnicalContactPhoneExtText'
  | 'TechnicalContactPhoneText'
> {
  if (!facility.certifierName || !facility.certifierTitle) {
    warnings.push({
      warningID: 'TW1125',
      message: `Facility is missing certifier information.`,
      type: 'facility',
    });
  }

  if (!facility.publicContactName || !facility.publicContactEmail || !facility.publicContactPhone) {
    warnings.push({
      warningID: 'TW1126',
      message: `Facility is missing public contact information.`,
      type: 'facility',
    });
  }

  if (!facility.technicalContactName || !facility.technicalContactEmail || !facility.technicalContactPhone) {
    warnings.push({
      warningID: 'TW1127',
      message: `Facility is missing technical contact information.`,
      type: 'facility',
    });
  }

  return {
    CertifierName: facility.certifierName,
    CertifierTitleText: facility.certifierTitle,
    CertificationSignedDate: new Date().toISOString().slice(0, 10) as ISO8601DateOnly,

    PublicContactNameText: {
      IndividualIdentifier: null, // It's unclear what this is.
      IndividualTitleText: null, // I think this is overkill, let's just use the full name field.
      NamePrefixText: null, // I think this is overkill, let's just use the full name field.
      NameSuffixText: null, // I think this is overkill, let's just use the full name field.
      IndividualName: {
        choice: 'IndividualFullName',
        value: facility.publicContactName,
      },
    },
    PublicContactEmailAddressText: facility.publicContactEmail,
    PublicContactPhoneText: facility.publicContactPhone,
    PublicContactPhoneExtText: facility.publicContactPhoneExt,

    TechnicalContactNameText: {
      IndividualIdentifier: null, // It's unclear what this is.
      IndividualTitleText: null, // I think this is overkill, let's just use the full name field.
      NamePrefixText: null, // I think this is overkill, let's just use the full name field.
      NameSuffixText: null, // I think this is overkill, let's just use the full name field.
      IndividualName: {
        choice: 'IndividualFullName',
        value: facility.technicalContactName,
      },
    },
    TechnicalContactEmailAddressText: facility.technicalContactEmail,
    TechnicalContactPhoneText: facility.technicalContactPhone,
    TechnicalContactPhoneExtText: facility.technicalContactPhoneExt,
  };
}
